// Sentry init for share-web. Imported as the first line of main.tsx so that
// Sentry is wired before any module that could throw at import time.
//
// share-web is a public-link surface — no user identity is associated with
// events. Same PHI scrubbing posture as admin-web; session replay is OFF.
// Empty DSN = no-op, safe for local dev.

import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? 'debug',
    // Only set `release` when present — under exactOptionalPropertyTypes an
    // explicit `undefined` is not assignable to the optional option.
    ...(release ? { release } : {}),
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
        if (event.request.url) {
          event.request.url = stripUrlParams(event.request.url);
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        if (breadcrumb.data) {
          delete (breadcrumb.data as Record<string, unknown>).request_body;
          delete (breadcrumb.data as Record<string, unknown>).response_body;
          delete (breadcrumb.data as Record<string, unknown>).body;
        }
      }
      return breadcrumb;
    },
  });
}

function stripUrlParams(url: string): string {
  try {
    const u = new URL(url);
    u.search = '';
    return u.toString();
  } catch {
    return url;
  }
}

export { Sentry };
